<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { XMarkIcon, ChevronDownIcon, AcademicCapIcon } from '@heroicons/vue/24/outline';

interface Props {
  isOpen: boolean;
  currentGradeLevel: string;
  currentTrack: string;
  isSaving?: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'save', gradeLevel: string, track: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const gradeLevel = ref('');
const track = ref('');

// Grade levels for Senior High School (ordinal format only)
const gradeLevels = [
  '11th',
  '12th'
];

// Academic tracks (K-12 Senior High School)
const tracks = [
  'STEM',
  'HUMSS',
  'ABM',
  'GAS',
  'TVL'
];

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    gradeLevel.value = props.currentGradeLevel;
    track.value = props.currentTrack;
  }
});

const isValid = computed(() => {
  return gradeLevel.value.trim().length > 0 && track.value.trim().length > 0;
});

// Preview display - handles ordinal suffixes correctly
const displayPreview = computed(() => {
  const grade = gradeLevel.value ? (/\d+(st|nd|rd|th)$/i.test(gradeLevel.value) ? gradeLevel.value : `Grade ${gradeLevel.value}`) : '';
  const trackText = track.value ? track.value.toUpperCase() : '';
  return [grade, trackText].filter(Boolean).join(' - ') || 'Not specified';
});

const handleSave = () => {
  if (isValid.value) {
    emit('save', gradeLevel.value.trim(), track.value.trim());
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 z-[9999]"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-2xl my-4 shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black max-w-md w-full transform transition-all"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-6 pt-4">
          <h2 class="text-2xl font-bold text-gray-900">Edit Grade & Track</h2>
          <button
            @click="handleClose"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- Content -->
        <div class="p-6 space-y-4">
          <!-- Grade Level Select -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Grade Level
            </label>
            <div class="relative">
              <select
                v-model="gradeLevel"
                class="w-full pl-10 pr-10 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white appearance-none text-gray-900 font-medium hover:border-blue-400 cursor-pointer"
              >
                <option value="" disabled class="text-gray-500">Select grade level...</option>
                <option v-for="grade in gradeLevels" :key="grade" :value="grade" class="text-gray-900">
                  {{ grade }}
                </option>
              </select>
              <AcademicCapIcon class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <ChevronDownIcon class="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <!-- Academic Track Select -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Academic Track
            </label>
            <div class="relative">
              <select
                v-model="track"
                class="w-full pl-10 pr-10 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white appearance-none text-gray-900 font-medium hover:border-blue-400 cursor-pointer"
              >
                <option value="" disabled class="text-gray-500">Select academic track...</option>
                <option v-for="trackOption in tracks" :key="trackOption" :value="trackOption" class="text-gray-900">
                  {{ trackOption }}
                </option>
              </select>
              <AcademicCapIcon class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <ChevronDownIcon class="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <!-- Preview -->
          <div v-if="gradeLevel && track" class="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
            <div class="flex items-start gap-3">
              <div class="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <AcademicCapIcon class="w-6 h-6 text-white" />
              </div>
              <div>
                <p class="text-xs font-semibold text-blue-900 mb-1">Preview</p>
                <p class="text-sm font-bold text-blue-700">{{ displayPreview }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6">
          <button
            @click="handleClose"
            class="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            @click="handleSave"
            :disabled="!isValid || isSaving"
            class="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-md flex items-center gap-2"
          >
            <svg v-if="isSaving" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ isSaving ? 'Saving...' : 'Save Changes' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.9);
}
</style>

